import Button from '@/components/base/Button';
import { Pill } from '@/components/base/Badges';
import Switch from '@/components/base/Switch';
import type { PillTone } from '@/components/base/Badges';
import { driversList } from '@/mocks/drivers';

type Driver = (typeof driversList)[number];

const statusToneMap: Record<Driver['status'], PillTone> = {
  available: 'success',
  busy: 'warning',
  offline: 'neutral',
  disabled: 'danger',
};

export default function DriversTable({
  drivers,
  onView,
  onEdit,
  onResetPassword,
  onToggleStatus,
}: {
  drivers: Driver[];
  onView: (driver: Driver) => void;
  onEdit: (driver: Driver) => void;
  onResetPassword: (driver: Driver) => void;
  onToggleStatus: (driver: Driver) => void;
}) {
  if (drivers.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-card border border-background-200 bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-right">
          <thead>
            <tr className="bg-background-50">
              {['الصورة', 'الاسم', 'رقم الهاتف', 'الحالة', 'المهام المنجزة', 'آخر نشاط', 'إجراءات'].map(
                (header) => (
                  <th
                    key={header}
                    className="whitespace-nowrap px-4 py-3 font-semibold text-[12px] text-foreground-500"
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => {
              const isDisabled = driver.status === 'disabled';

              return (
                <tr
                  key={driver.id}
                  className="cursor-pointer border-t border-background-100 transition-colors hover:bg-background-50"
                  onClick={() => onView(driver)}
                >
                  <td className="px-4 py-3.5">
                    <div className="h-10 w-10 overflow-hidden rounded-full border border-background-200">
                      <img
                        src={driver.photo}
                        alt={driver.name}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className="block text-[14px] font-medium text-foreground-950">
                      {driver.name}
                    </span>
                    <span className="mt-0.5 block text-[12px] text-foreground-400">
                      {driver.vehicle}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-[13px] text-foreground-700" dir="ltr">
                    {driver.phone}
                  </td>
                  <td className="px-4 py-3.5">
                    <Pill tone={statusToneMap[driver.status]}>{driver.statusLabel}</Pill>
                  </td>
                  <td className="px-4 py-3.5 text-[13px] text-foreground-900">
                    {driver.tasksToday} مهمة اليوم
                  </td>
                  <td className="px-4 py-3.5 text-[12px] text-foreground-500">منذ 5 دقائق</td>
                  <td className="px-4 py-3.5" onClick={(event) => event.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onView(driver)}
                        className="flex h-8 items-center gap-1 rounded-md border border-background-300 px-2.5 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-50"
                      >
                        <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                        عرض
                      </button>
                      <button
                        type="button"
                        onClick={() => onEdit(driver)}
                        className="flex h-8 items-center gap-1 rounded-md border border-secondary-300 px-2.5 text-[12px] font-medium text-secondary-600 transition-colors hover:bg-secondary-50"
                      >
                        <i className="ri-pencil-line text-sm" aria-hidden="true"></i>
                        تعديل
                      </button>
                      <Switch
                        checked={!isDisabled}
                        onChange={() => onToggleStatus(driver)}
                        size="sm"
                      />
                      <button
                        type="button"
                        onClick={() => onResetPassword(driver)}
                        className="text-[12px] font-medium text-secondary-500 underline underline-offset-2 transition-colors hover:text-secondary-700"
                      >
                        إعادة تعيين كلمة المرور
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}